import { axios } from "..";
import type {
  EmailLogStatusHistoryEntry,
  IEmailLog,
} from "../interfaces/models/IEmailLog";

export type EmailLogStatsMetricCounts = {
  requests: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounces: number;
  spam: number;
};

export type EmailLogStatsMonthBucket = EmailLogStatsMetricCounts & {
  year: number;
  month: number;
  label: string;
};

export type EmailLogStatsResponse = {
  windowMonths: number;
  since: number;
  totals: EmailLogStatsMetricCounts & {
    deliveredPct: number;
    openedPct: number;
    clickedPct: number;
    bouncesPct: number;
    spamPct: number;
  };
  byMonth: EmailLogStatsMonthBucket[];
};

export type EmailLogDetailResponse = IEmailLog & {
  eventTitle?: string | null;
  scheduledAt?: number | null;
  from?: string | null;
  subject?: string | null;
  dateSent?: number | null;
  dateScheduled?: number | null;
  dateDelivered?: number | null;
  timeline: EmailLogStatusHistoryEntry[];
};

/** Avoid duplicate concurrent fetches (React Strict Mode remounts the dashboard effect). */
const STATS_CACHE_TTL_MS = 60_000;
let statsInFlight: Promise<EmailLogStatsResponse | null> | null = null;
let statsCache: { at: number; data: EmailLogStatsResponse | null } | null = null;

export const getEmailLogById = async (
  id: string,
): Promise<EmailLogDetailResponse | null> => {
  try {
    const params = new URLSearchParams({ id });
    const response = await axios.Get(`./emailLog?${params.toString()}`);
    return (response.data ?? null) as EmailLogDetailResponse | null;
  } catch {
    return null;
  }
};

export const getEmailLogStats = async (): Promise<EmailLogStatsResponse | null> => {
  if (statsCache && Date.now() - statsCache.at < STATS_CACHE_TTL_MS) {
    return statsCache.data;
  }
  if (statsInFlight) {
    return statsInFlight;
  }

  statsInFlight = (async () => {
    try {
      const response = await axios.Get("./emailLog?stats=true");
      const data = (response.data ?? null) as EmailLogStatsResponse | null;
      statsCache = { at: Date.now(), data };
      return data;
    } catch {
      return null;
    } finally {
      statsInFlight = null;
    }
  })();

  return statsInFlight;
};
